/**
 * header-regime.js — the header's layout by WIDTH at page load: the phone layout below 1024, the desktop header from 1024
 * on every device, touch included, as the source (section 15, theme 89.0, 22.9.2026; head 52.0 chose B); and a touch tap on
 * a top-level item with a submenu opens it instead of following the link (below).
 *
 * Why this file: the first data-menu-style is written by an inline script in layout/theme.liquid (Tinker's mirror of
 * setHeaderMenuStyle - touch or an overflowing menu -> 'drawer'), and layout/ is not touched without Elad
 * (theme/CLAUDE.md 2.1, head 52.0 condition f). assets/utilities.js' setHeaderMenuStyle() now decides by width
 * (commit 97e78a25) and runs on resize; this calls it once at load, after the inline script, so the final layout is the
 * source's. What the inline script shows first, until this runs, is measured in review-hd-89 (hd_regime_flash_89).
 */
import { setHeaderMenuStyle } from '@theme/utilities';

setHeaderMenuStyle();

/*
 * On a touch screen from 1024 the desktop header now shows (above), and a TAP on a top-level item with a submenu must
 * OPEN it, as on the source (its item is a <summary>: a tap opens the mega menu, same URL - hd_tap_menu_89.mjs, 1180).
 * Tinker opens the submenu on pointerenter, which a touch tap fires too, and then the same tap's click follows the
 * link (measured: URL -> /collections). So: a touch tap on such an item never navigates - a tap on a closed item opens
 * it, a tap on an open item closes it, as the source's <summary> toggles (theme 90.0, part 3: the item's page is now
 * reached by the 'view all' link inside the mega menu, snippets/header-mega-inova.liquid).
 */
const menu = document.querySelector('header-component header-menu');

/*
 * theme 90.0 (part 3): the dropdown (the source's 'Explore', .menu-list__submenu--hdd) hangs from its own item but must
 * start at the header's TOP, as the source's .dropdown (absolute, top 0, padding = the header's height). When the menu
 * wraps to two rows (1024-1535) an item's distance from the header's top depends on its row, which CSS cannot know - so
 * it is measured when the item is entered or focused and written to the item as --hdd-top (base.css reads it).
 */
if (menu) {
  const header = menu.closest('header-component');
  /*
   * theme 178.0 (#77, Head 81.0): the panel starts 32 px past its item's START edge and grows toward the end (the source's
   * .dropdown). For the item at the far end (in rtl the left-most) that runs off the screen. The source's own escape is
   * `.dropdown.left-0` - anchor to the item's END edge (theme.css, logical props); base.css reads [data-hdd-end]. Decided
   * here, before the item opens, from the live panel rect while it is still hidden (visibility: hidden keeps its layout);
   * no attribute = the default placement, so a blocked script leaves today's panel. review-178/menu/PROPOSAL-edge.md.
   *
   * Head 81.0: decided once per item entered (place runs on every pointerover - a rect read each time would force a layout
   * per child move); a resize forgets it, so the next enter decides again.
   */
  let decided = null;
  const edge = (item) => {
    const sub = item.querySelector(':scope > .menu-list__submenu--hdd');
    if (!sub) return;
    item.removeAttribute('data-hdd-end');
    const r = sub.getBoundingClientRect();
    if (r.left < 0 || r.right > document.documentElement.clientWidth) item.setAttribute('data-hdd-end', '');
  };
  const place = (event) => {
    if (!(event.target instanceof Element) || !header) return;
    const item = event.target.closest('.menu-list__list-item');
    if (!item || !item.querySelector(':scope > .menu-list__submenu--hdd')) return;
    // 218.0: the two rects are DRAWN px; --hdd-top is a LAYOUT length inside the item - / the item's zoom (MDN Element.currentCSSZoom:
    // the site zoom from 1280, the header's own .67 at 1024-1279; 1 elsewhere) - the hotspot-linea 4ad56cd0 helper. Measured
    // (Home 24 TABLE24 #9 + 218.0): the dropdown 7.3 drawn px low at 1518 AND 7.1 at 1279 (the .67 band) vs 0 at 2335.
    item.style.setProperty('--hdd-top', `${(header.getBoundingClientRect().top - item.getBoundingClientRect().top) / (item.currentCSSZoom || 1)}px`);
    if (item !== decided) { decided = item; edge(item); }
  };
  menu.addEventListener('pointerover', place, true);
  menu.addEventListener('focusin', place, true);
  window.addEventListener('resize', () => {
    decided = null;
    const open = menu.querySelector('.menu-list__link[aria-expanded="true"]');
    const item = open && open.closest('.menu-list__list-item');
    if (item) edge(item);
  });
}

if (menu) {
  let wasOpen = null;
  menu.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'touch' || !(event.target instanceof Element)) return;
      const link = event.target.closest('.menu-list__link[aria-controls]');
      wasOpen = link ? link.getAttribute('aria-expanded') === 'true' : null;
    },
    true
  );
  menu.addEventListener(
    'click',
    (event) => {
      if (wasOpen === null || !(event.target instanceof Element)) return;
      if (event.target.closest('.menu-list__link[aria-controls]')) {
        event.preventDefault();
        // theme 178.0 (#77): a tap now also OPENS a closed item - header-menu.js ignores the finger’s enter / leave.
        // activate() finds its item INSIDE event.target (findMenuItem: target.querySelector('[ref="menuitem"]')) - it expects
        // the <li>, as component.js’s on: delegation hands it; a tap’s own target is the link or its text span.
        if (wasOpen) menu.deactivate(event);
        else {
          const li = event.target.closest('.menu-list__list-item');
          if (li) menu.activate({ target: li, type: 'tap' });
        }
      }
      wasOpen = null;
    },
    true
  );
}
